import { formatPrice } from "@bcc/lib/utils";
import type { CurrencyObject } from "@client-shared/lib/constants";
import { exportToCsv, type CsvColumn } from "@client-shared/utils";
import type { PurchaseOrder } from "@shared/types";
import type { TFunction } from "i18next";

type OrderCsvRow = Record<string, unknown>;

/** Expand orders: one row per line item; one row (line-item fields empty) if no line items */
const buildOrderCsvRows = (orders: PurchaseOrder[]): OrderCsvRow[] => {
  const rows: OrderCsvRow[] = [];
  for (const order of orders) {
    if (order.lineItems?.length) {
      for (const item of order.lineItems) {
        rows.push({ ...(order as unknown as OrderCsvRow), lineItems: item });
      }
    } else {
      rows.push({ ...(order as unknown as OrderCsvRow), lineItems: undefined });
    }
  }
  return rows;
};

export function exportOrdersToCsv(orders: unknown[], t: TFunction, currencyObject?: CurrencyObject): void {
  const rows = buildOrderCsvRows(orders as PurchaseOrder[]);

  const columns: CsvColumn<OrderCsvRow>[] = [
    { key: "batchId", header: t("views.orders.exportColumns.batchId") },
    {
      key: "purchaseOrderId",
      header: t("views.orders.exportColumns.purchaseOrderId"),
    },
    { key: "orderStatus", header: t("views.orders.exportColumns.orderStatus") },
    {
      key: "shippingAddress.zip",
      header: t("views.orders.exportColumns.shippingAddress.zip"),
    },
    {
      key: "shippingAddress.country",
      header: t("views.orders.exportColumns.shippingAddress.country"),
    },
    {
      key: "shippingAddress.countryCode",
      header: t("views.orders.exportColumns.shippingAddress.countryCode"),
      formatter: (value, row) => {
        if (typeof value === "string") return value;
        const addr = row.shippingAddress as { country?: string | null } | undefined;
        return addr?.country ?? "";
      },
    },
    {
      key: "shippingAddress.province",
      header: t("views.orders.exportColumns.shippingAddress.province"),
    },
    {
      key: "shippingAddress.address2",
      header: t("views.orders.exportColumns.shippingAddress.address2"),
    },
    {
      key: "shippingAddress.city",
      header: t("views.orders.exportColumns.shippingAddress.city"),
    },
    {
      key: "shippingAddress.phone",
      header: t("views.orders.exportColumns.shippingAddress.phone"),
    },
    {
      key: "shippingAddress.address1",
      header: t("views.orders.exportColumns.shippingAddress.address1"),
    },
    {
      key: "shippingAddress.name",
      header: t("views.orders.exportColumns.shippingAddress.name"),
    },
    {
      key: "shippingAddress.company",
      header: t("views.orders.exportColumns.shippingAddress.company"),
    },
    {
      key: "orderTotal",
      header: t("views.orders.exportColumns.orderTotal"),
      formatter: currencyObject ? (value) => formatPrice(value as string, currencyObject.symbol, currencyObject.iso) : undefined,
    },
    {
      key: "shippingAmount",
      header: t("views.orders.exportColumns.shippingAmount"),
    },
    { key: "createdAt", header: t("views.orders.exportColumns.createdAt") },
    {
      key: "orderWeightInGrams",
      header: t("views.orders.exportColumns.orderWeightInGrams"),
    },
    {
      key: "customerAccountName",
      header: t("views.orders.exportColumns.customerAccountName"),
    },
    {
      key: "lineItems.quantity",
      header: t("views.orders.exportColumns.lineItems.quantity"),
    },
    {
      key: "lineItems.carrier",
      header: t("views.orders.exportColumns.lineItems.carrier"),
    },
    {
      key: "lineItems.trackingNumber",
      header: t("views.orders.exportColumns.lineItems.trackingNumber"),
    },
    {
      key: "lineItems.trackingUrl",
      header: t("views.orders.exportColumns.lineItems.trackingUrl"),
    },
    {
      key: "lineItems.lineItemWeightInGrams",
      header: t("views.orders.exportColumns.lineItems.lineItemWeightInGrams"),
    },
    {
      key: "lineItems.productTitle",
      header: t("views.orders.exportColumns.lineItems.productTitle"),
    },
    {
      key: "lineItems.grindType",
      header: t("views.orders.exportColumns.lineItems.grindType"),
    },
    {
      key: "lineItems.labelCreatedDate",
      header: t("views.orders.exportColumns.lineItems.labelCreatedDate"),
    },
    {
      key: "lineItems.sourceProductSku",
      header: t("views.orders.exportColumns.lineItems.sourceProductSku"),
    },
    {
      key: "lineItems.exportLabelUrl",
      header: t("views.orders.exportColumns.lineItems.exportLabelUrl"),
    },
  ];

  exportToCsv({
    data: rows,
    filename: "orders",
    columns: columns as unknown as CsvColumn<Record<string, unknown>>[],
  });
}
